<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePolarisConfigStore } from 'dashboard/store/polaris/preferences';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';

// Phase 9.4: the model is chosen with the kit's OptionSelect. The recommended model says so in its name and a
// model that is coming soon cannot be chosen, as before. (The provider logos and the per-message credits, shown
// on Chatwoot Cloud only, do not fit the kit's select and are left out.)
const props = defineProps({
  featureKey: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);

const { t } = useI18n();
const polarisConfigStore = usePolarisConfigStore();

const availableModels = computed(() =>
  polarisConfigStore.getModelsForFeature(props.featureKey)
);

const recommendedModelId = computed(() =>
  polarisConfigStore.getDefaultModelForFeature(props.featureKey)
);

const selectedModel = computed(() =>
  polarisConfigStore.getSelectedModelForFeature(props.featureKey)
);

const selectedModelId = ref(null);

watch(
  selectedModel,
  newSelected => {
    if (newSelected) {
      selectedModelId.value = newSelected;
    }
  },
  { immediate: true }
);

const options = computed(() =>
  availableModels.value.map(model => {
    let label = model.display_name;
    if (model.id === recommendedModelId.value) {
      label = `${label} · ${t('GENERAL.PREFERRED')}`;
    } else if (model.coming_soon) {
      label = `${label} · ${t('POLARIS_SETTINGS.MODEL_CONFIG.COMING_SOON')}`;
    }
    return { value: model.id, label, disabled: !!model.coming_soon };
  })
);

const selectModel = modelId => {
  selectedModelId.value = modelId;
  emit('change', { feature: props.featureKey, model: modelId });
};
</script>

<template>
  <OptionSelect
    :model-value="selectedModelId ?? ''"
    :options="options"
    :placeholder="t('POLARIS_SETTINGS.MODEL_CONFIG.SELECT_MODEL')"
    :aria-label="label || undefined"
    class="w-full sm:w-60"
    @update:model-value="selectModel"
  />
</template>
